import { useState } from 'react';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { cn } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import { ChevronLeftIcon } from '@/icons';
import { type CategoryTree } from '@/shared/category/type';
import { type MapTemplate, type MapTemplateType } from '@/types/MapTemplate';
import { DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { type BuildType } from '@/views/build/context/BuildInfoProvider';
import { type SelectionSchoolInfo } from '../../../type';
import { type CategoryInfoInputType } from '../../category-info';
import { CategoryInfoView } from '../../category-select-view';
import { MapSelectionView } from '../../map-selection';
type CurrentView = 'MAP' | 'EDUCATION';
export interface QuizSetupModalProps {
  /** 맵 템플릿 목록 */
  mapTemplates: MapTemplate[];
  /** 선택된 맵 템플릿 ID (선택사항) */
  selectedMapTemplateId?: string;
  /** build form 정보들 */
  initialCategoryInfoValues: CategoryInfoInputType;
  /** BuildInfo의 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** BuildInfo의 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** BuildInfo의 출판사 정보 */
  publishers: SelectionSchoolInfo['publishers'];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;
  /** 빌드 진입 타입 */
  buildType: BuildType;
  /** 맵 선택 완료 시 호출 (선택된 맵 ID와 타입 전달) */
  onMapComplete: (selectedMapTemplateId: string, mapTemplateType: MapTemplateType) => void;
  /** 교육정보 입력 완료 시 호출 */
  onCategoryInfoComplete: (values: CategoryInfoInputType) => void;
  /** 모달 닫기 시 호출 */
  onClose?: () => void;
}

/**
 * QuizSetupModal - 신규 퀴즈 생성 통합 모달
 *
 * 맵 선택 → 교육정보 입력 플로우를 하나의 모달에서 처리합니다.
 * currentView 상태로 MAP과 EDUCATION 뷰를 전환하며,
 * 사용자는 뒤로가기 버튼으로 맵 선택 단계로 돌아갈 수 있습니다.
 *
 * @param mapTemplates - 사용 가능한 맵 템플릿 배열
 * @param selectedMapTemplateId - 현재 선택된 맵 템플릿 ID
 * @param schoolInfoTree - BuildInfo의 학교 정보 트리
 * @param subjectTree - BuildInfo의 과목 트리
 * @param publishers - BuildInfo의 출판사 정보
 * @param userCountryCode - 사용자 국가 코드
 * @param onMapComplete - 맵 선택 완료 시 호출
 * @param onCategoryInfoComplete - 카테고리 정보 입력 완료 시 호출
 * @param onClose - 모달 닫기 시 호출
 */
export const QuizSetupModal = ({
  mapTemplates,
  selectedMapTemplateId,
  initialCategoryInfoValues,
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
  buildType,
  onMapComplete,
  onCategoryInfoComplete
}: QuizSetupModalProps) => {
  const {
    t
  } = useTranslation(['build']);
  const [currentView, setCurrentView] = useState<CurrentView>('MAP');

  // 맵 선택 완료 핸들러
  const handleMapComplete = (selectedMapTemplateId: string, mapTemplateType: MapTemplateType) => {
    // 상위에 맵 선택 완료 알림
    onMapComplete(selectedMapTemplateId, mapTemplateType);

    // 맵 선택 완료 이벤트 트래킹 (main 브랜치와 동일)
    logger.track('map_select_completed', {
      from: 'enter_build_page',
      is_change: false,
      build_type: buildType
    });

    // 카테고리 정보 입력 모달 오픈 이벤트 트래킹
    logger.track('category_input_modal_open', {
      trigger: 'new'
    });

    // CategoryInfoView로 전환
    setCurrentView('EDUCATION');
  };

  // 뒤로가기 핸들러 (CategoryInfoView → MapSelectionView)
  const handleBack = () => {
    setCurrentView('MAP');
  };

  // 현재 뷰에 따른 제목
  const title: string = currentView === 'MAP' ? t('build:mapTemplate.selectQuizMap') as string : t('build:build.quizSetupModal.additionalInfoTitle') as string;
  return <ResponsiveDialog size="2xl" className="h-[90%] max-h-[800px] tablet-sm:gap-xs" onClose={() => {}} data-sentry-element="ResponsiveDialog" data-sentry-component="QuizSetupModal" data-sentry-source-file="QuizSetupModal.tsx">
      <DialogHeader className={cn(currentView === 'EDUCATION' && 'mobile-lg:pt-md')} leftSlot={currentView === 'EDUCATION' ? <IconButton variant="transparent" intent="neutral" size="sm" onClick={handleBack}>
              <ChevronLeftIcon size="md" />
            </IconButton> : undefined} title={title} hasCloseButton={false} onClose={() => {}} data-sentry-element="DialogHeader" data-sentry-source-file="QuizSetupModal.tsx" />

      {currentView === 'MAP' ? <MapSelectionView mapTemplates={mapTemplates} selectedMapTemplateId={selectedMapTemplateId} userCountryCode={userCountryCode} onComplete={handleMapComplete} /> : <CategoryInfoView formValues={initialCategoryInfoValues} schoolInfoTree={schoolInfoTree} subjectTree={subjectTree} publishers={publishers} userCountryCode={userCountryCode} onComplete={onCategoryInfoComplete} trigger="new" />}
    </ResponsiveDialog>;
};